import { formatPromotionDate } from "./utils/supplierPromotion";
import React, {useState} from "react";
import {connect} from "react-redux";
import {makeStyles} from "@material-ui/core/styles";
import {useParams, useLocation} from "react-router-dom";
import {
	Grid,
	Button,
	TextField,
	CircularProgress,
	Tabs,
	Tab,
	Typography,
	Box,
	Checkbox,
	FormControlLabel,
	IconButton,
	Divider,
	Popper,
	Fade,
	Paper,
	Spinner
} from "@material-ui/core";
import PropTypes from "prop-types";
import {Formik, Form as FormikForm} from "formik";
import "react-datetime/css/react-datetime.css";
import FlagIconFactory from "react-flag-icon-css";
import "./style.css";
import {
	addProduct,
	getBrands,
	getCountries,
	getCategory,
	getSubCategory,
	getSuppliers,
	getSubCategoryMap,
	setError,
	setLoading, updateProduct, getProductToRelate, getRelatedProducts, addRelatedProduct, removeRelatedProduct,
} from "../../../store/actions/dashboardAction";

import colors from "../../../theme/colors";
import EditorTool from "../../../components/EditorTool";
import SelectAndSearchComponent from "../../../components/SelectAndSearchComponent/SelectAndSearchComponent";
import useRelatedProducts from "./hook/useRelatedProducts";
import RelatedProduct from "./relatedProducts";
import useProducts from "./hook/useProducts";
import ImagesCarousel from "./components/ImagesCarousel";
import KhmerRichTextField from "./components/KhmerRichTextField";
import CustomDatetime from "../../../components/DatePicker/DatePicker";
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import styled from "styled-components";
import Form from 'react-bootstrap/Form';
import Col from 'react-bootstrap/Col';
import DeleteIcon from '@material-ui/icons/Delete';
import ToggleOnOffButton from "../../../components/Button/ToggleOnOffButton";
import CreatableSelectComponent from "../../../components/SelectAndSearchComponent/CreatableSelectComponent";
import LightPickerInput from '../../../components/DateRangePicker/LightPickerInput';
import { QueryBuilder, Clear } from "@material-ui/icons";
import Table from 'react-bootstrap/Table';
import { margin } from "styled-system";

const FlagIcon = FlagIconFactory(React, {useCssModules: false});

// TabPanel helper
function TabPanel(props) {
	const {children, value, index, ...other} = props;
	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`full-width-tabpanel-${index}`}
			aria-labelledby={`full-width-tab-${index}`}
			{...other}
		>
			{value === index && (
				<Box p={3}>
					<Typography component="div">{children}</Typography>
				</Box>
			)}
		</div>
	);
}

// styles
const useStyles = makeStyles(() => ({
	root: {
		padding: "1rem",
		backgroundColor: colors.white,
		// marginTop: "1rem",
		"& .MuiTab-textColorPrimary.Mui-selected": {
			color: `${colors.primary} !important`,
		},
		"& .MuiTabs-indicator": {
			backgroundColor: colors.primary,
		},
	},
	field: {
		marginBottom: "1rem"
	},
	fileInput: {
		display: "block",
		marginTop: "8px",
		marginBottom: "8px",
	},
	rightCard: {
		padding: "4px",
		borderRadius: "4px",
		outline: `1px solid ${colors.darkLight}`,
	},
	typography: {
		padding: "8px",
	},
}));

// Styled components for supplier section
const columnWidths = {
	supplier: "11.5%",
	quantity: "5%",
	unit: "5%",
	productNumber: "9.5%",
	productName: "9.5%",
	comment: "9.5%",
	retailPrice: "6.5%",
	unitCost: "6.5%",
	promotionPriceDate: "20%",
	stock: "3.5%",
	order: "3.5%",
	action: "9.5%",
};

const SupplierRowScroll = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 1em;
`;

const SupplierRowFlex = styled(Form.Row)`
  display: flex;
  flex-wrap: nowrap;
  min-width: fit-content;
  width: 100%;
  margin-bottom: 5px;
`;

const SupplierCol = styled(Form.Group)`
  min-width: ${({ colName }) => columnWidths[colName] || "120px"};
  width: ${({ colName }) => columnWidths[colName] || "120px"};
  flex: 0 0 ${({ colName }) => columnWidths[colName] || "120px"};
  margin-bottom: 0 !important;
  padding-left: 5px;
`;

const FormLabel = styled(Form.Label)`
  font-size: 14px;
`;

const DraggableSupplierRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  min-width: fit-content;
  width: 100%;
  margin-bottom: 5px;
  background: ${props => props.isDragging ? '#f8f9fa' : 'white'};
  border: ${props => props.isDragging ? '2px dashed #007bff' : '1px solid transparent'};
  border-radius: 4px;
  padding: ${props => props.isDragging ? '8px' : '0'};
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #f8f9fa;
  }
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  flex: 0 0 30px;
  padding-left: 5px;
  cursor: grab;
  color: #6c757d;
  
  &:active {
    cursor: grabbing;
  }
  
  &::before {
    content: "⋮⋮";
    font-size: 14px;
    line-height: 0.5;
    letter-spacing: 2px;
  }
`;

const DragHandleSpacer = styled.div`
  width: 30px;
  flex: 0 0 30px;
  padding-left: 5px;
`;

const ProductManagement = (props) => {
	const classes = useStyles();
	const location = useLocation();
	const {type} = useParams(); // destructure the param
	// Create a URLSearchParams object from the query string
	const queryParams = new URLSearchParams(location.search);

	// Get the value of "item_id"
	const itemId = queryParams.get('item_id');

	const [tabValue, setTabValue] = useState(0);

	// Initialize related products hook first (needed by useProducts)
	const [relatedProductsState, setRelatedProductsState] = useState(null);

	const {
		initialValues,
		countriesOptions,
		ProductSchemas,
		imageFile,
		setImageFile,
		sliderFiles,
		setSliderFiles,
		uploadProductImage,
		uploadProductSliders,
		imageUploading,
		sliderUploading,
		isAlreadySKU,
		getDietaryPreferences,
		dietaryPreferences,
		handleSubmit,
		loading,
		handleRemoveSliders,
		calculateDuration,
		handleSubmitWithConfirmation,
		// Supplier section props
		supplierSections,
		errors,
		supplierProductNumberMap,
		supplierProductNameMap,
		isDisabled,
		isLoading,
		deletePromotionHistory,
		deletingPromotionHistory,
		promotionHistoryRefs,
		openPromotionHistory,
		promotionHistoryData,
		loadingPromotionHistory,
		priceHistoryRefs,
		openPriceHistory,
		priceHistoryData,
		loadingPriceHistory,
		handleAddSupplierSection,
		handleRemoveSupplierSection,
		updateSupplierData,
		toggleSupplierEnabled,
		handleOnDragEnd,
		handlePromotionHistoryEnter,
		handlePromotionHistoryLeave,
		setPromotionHistoryRef,
		clearPromotionData,
		handlePriceHistoryEnter,
		handlePriceHistoryLeave,
		setPriceHistoryRef,
		stockListRef,
		openStockList,
		setOpenStockList,
		stockList,
		acceptAndPackedOrder,
		availableForSale,
	} = useProducts(props, type, itemId, relatedProductsState);

	const relatedProducts = useRelatedProducts(props, initialValues, itemId, type);

	// Update the related products state when it changes
	React.useEffect(() => {
		setRelatedProductsState(relatedProducts);
	}, [relatedProducts.selectedProducts]);

	const handleTabChange = (e, v) => setTabValue(v);

	if (loading) return <CircularProgress/>;
	return (
		<div className={classes.root}>
			<Tabs value={tabValue} indicatorColor="primary" textColor="primary" onChange={handleTabChange}>
				<Tab value={0} label="Basic Info"/>
				<Tab value={1} label="Related Products"/>
			</Tabs>
			<Formik
				initialValues={initialValues}
				validationSchema={ProductSchemas}
				onSubmit={handleSubmitWithConfirmation}
				validateOnChange={false}
				validateOnBlur={false}
				enableReinitialize={true}
			>
				{({values, errors, touched, handleChange, setFieldValue, isSubmitting, setFieldError, setFieldTouched}) => {
					return (
						<FormikForm>
							<TabPanel value={tabValue} index={0}>
								<Typography variant={"h5"} className="mb-1 page-title">
									Products {type.toUpperCase()}
								</Typography>
								<Grid container spacing={3}>
									<Grid item xs={12} md={9}>
										<Grid container spacing={2}>
											{/* Name */}
											<Grid item xs={12} md={6}>
												<Typography>Name</Typography>
												<TextField
													fullWidth
													name="itemName"
													label="Name"
													variant="outlined"
													className={classes.field}
													value={values.itemName}
													onChange={handleChange}
													error={touched.itemName && Boolean(errors.itemName)}
													helperText={touched.itemName && errors.itemName}
													size={"small"}
												/>
											</Grid>
											{/* Name KH */}
											<Grid item xs={12} md={6}>
												<Typography>Name in Khmer</Typography>
												<TextField
													fullWidth
													name="itemNameKhmer"
													label="Name in Khmer"
													variant="outlined"
													className={classes.field}
													value={values.itemNameKhmer}
													onChange={handleChange}
													size={"small"}
												/>
											</Grid>
											{/* SKU */}
											<Grid item xs={12} md={6}>
												<Typography>SKU/Product code</Typography>
												<TextField
													fullWidth
													name="itemCode"
													label="SKU/Product code"
													variant="outlined"
													className={classes.field}
													value={values.itemCode}
													onChange={async (e) => {
														handleChange(e);
														const exists = await isAlreadySKU(e.target.value);
														if (exists === 1) {
															setFieldError("itemCode", "SKU already exists");
															setFieldTouched("itemCode", true, false); // 👈 force touched
														}
													}}
													error={touched.itemCode && Boolean(errors.itemCode)}
													helperText={touched.itemCode && errors.itemCode}
													size={"small"}
												/>
											</Grid>
											{/* Variant */}
											<Grid item xs={12} md={3}>
												<Typography>Variant name</Typography>
												<TextField
													fullWidth
													name="itemPriceName"
													label="Variant name"
													variant="outlined"
													className={classes.field}
													value={values.itemPriceName}
													onChange={handleChange}
													error={touched.itemPriceName && Boolean(errors.itemPriceName)}
													helperText={touched.itemPriceName && errors.itemPriceName}
													size={"small"}
												/>
											</Grid>
											{/* Weight */}
											<Grid item xs={12} md={3}>
												<Typography>Weight (g)</Typography>
												<TextField
													fullWidth
													type="number"
													name="itemWeight"
													label="Weight (g)"
													variant="outlined"
													className={classes.field}
													value={values.itemWeight}
													onChange={handleChange}
													error={touched.itemWeight && Boolean(errors.itemWeight)}
													helperText={touched.itemWeight && errors.itemWeight}
													size={"small"}
												/>
											</Grid>
											{/* Category */}
											<Grid item xs={12} md={6}>
												<Typography>Category</Typography>
												<SelectAndSearchComponent
													options={props.category}
													value={values.itemCat}
													onChange={(e) => {
														setFieldValue("itemCat", e);
														// also fetch subcategory map if action available
														props.getSubCategoryMap && props.getSubCategoryMap({cat_id: e?.cat_id});
														setFieldValue("itemSubCat", null);
													}}
													placeholder="Select Category"
													getOptionValue={(opt) => opt.list_categories?.[0]?.name}
													getOptionLabel={(cat) => (
														<div style={{display: "flex", alignItems: "center", gap: "5px"}}>
															<img src={cat?.list_categories?.[0]?.image} style={{width: "20px", objectFit: "contain"}}
															     alt=""/> {cat.list_categories?.[0]?.name}
														</div>
													)}
													error={errors.itemCat}
												/>
											</Grid>
											{/* Brand */}
											<Grid item xs={12} md={6}>
												<Typography>Brand</Typography>
												<SelectAndSearchComponent
													options={props.brands}
													value={values.itemBrand}
													onChange={(e) => setFieldValue("itemBrand", e ?? null)}
													placeholder="Select Brand"
												/>
											</Grid>

											{/* Sub Category */}
											<Grid item xs={12} md={6}>
												<Typography>Sub Category</Typography>
												<SelectAndSearchComponent
													options={props.sub_category}
													value={values.itemSubCat}
													onChange={(e) => setFieldValue("itemSubCat", e ?? null)}
													placeholder="Select SubCategory"
													getOptionValue={(opt) => opt.list_sub_categories?.[0]?.name}
													getOptionLabel={(opt) => (
														<div style={{display: "flex", alignItems: "center", gap: "5px"}}>
															<img
																src={opt?.list_sub_categories?.[0]?.image}
																style={{width: "20px", objectFit: "contain"}}
																alt=""
															/>
															{opt.list_sub_categories?.[0]?.name}
														</div>
													)}
													error={errors.itemSubCat}
												/>
											</Grid>

											{/* Country */}
											<Grid item xs={12} md={6}>
												<Typography>Country</Typography>
												<SelectAndSearchComponent
													placeholder="Select Country"
													value={values.itemCountry}
													onChange={(opt) => setFieldValue("itemCountry", opt)}
													options={countriesOptions}
													getOptionValue={(opt) => opt.name}
													getOptionLabel={(country) => (
														<div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
															<span style={{height: 'fit-content'}}>
																<FlagIcon code={country.code.toLowerCase()}/>
															</span>
															{country.name}
														</div>
													)}
													error={errors.itemCountry}
												/>
											</Grid>

											{/* About Item */}
											<Grid item xs={12} md={6}>
												<Typography variant="subtitle1">About Item</Typography>
												<EditorTool value={values.itemAbout} setValue={(val) => setFieldValue("itemAbout", val)}
												            page="products"/>
											</Grid>
											{/* About Item KH */}
											<Grid item xs={12} md={6}>
												<KhmerRichTextField
													label="About Item (KH)"
													value={values.itemAboutKm}
													onChange={(val) => setFieldValue("itemAboutKm", val)}
													error={errors.itemAboutKm}
												/>
											</Grid>
											{/* How To Use */}
											<Grid item xs={12} md={6}>
												<Typography variant="subtitle1">How To Use</Typography>
												<EditorTool value={values.howToUse} setValue={(val) => setFieldValue("howToUse", val)}
												            page="products"/>
											</Grid>
											{/* How To Use KH */}
											<Grid item xs={12} md={6}>
												<KhmerRichTextField
													label="How To Use (KH)"
													value={values.howToUseKm}
													onChange={(val) => setFieldValue("howToUseKm", val)}
													error={errors.howToUseKm}
												/>
											</Grid>
											{/* Ingredients */}
											<Grid item xs={12} md={6}>
												<Typography variant="subtitle1">Ingredients</Typography>
												<EditorTool value={values.ingredients} setValue={(val) => setFieldValue("ingredients", val)}
												            page="products"/>
											</Grid>
											{/* Ingredients KH */}
											<Grid item xs={12} md={6}>
												<KhmerRichTextField
													label="Ingredients (KH)"
													value={values.ingredientsKm}
													onChange={(val) => setFieldValue("ingredientsKm", val)}
													error={errors.ingredientsKm}
												/>
											</Grid>
											{/* Supplier */}
											{/* <Grid item xs={12} md={6}>
												<Typography>Supplier</Typography>
												<SelectAndSearchComponent
													options={props.suppliers}
													value={values.itemSupplierId}
													onChange={(e) => setFieldValue("itemSupplierId", e)}
													placeholder="Select Supplier"
													getOptionValue={(opt) => opt?.store_users[0]?.first_name}
													error={errors.itemSupplierId}
													getOptionLabel={(opt) => (
														<div style={{display: "flex", alignItems: "center", gap: "5px"}}>
															<img src={opt?.store_users[0]?.supplier_logo}
															     style={{width: "20px", objectFit: "contain"}}
															     alt=""/> {opt?.store_users[0]?.first_name}
														</div>
													)}
												/>
											</Grid> */}
											{/* Available Quantity */}
											{/* <Grid item xs={12} md={6}>
												<Typography>Available Quantity</Typography>
												<TextField
													fullWidth
													type="number"
													name="itemQuantity"
													label="Available Quantity"
													variant="outlined"
													className={classes.field}
													value={values.itemQuantity}
													onChange={handleChange}
													error={touched.itemQuantity && Boolean(errors.itemQuantity)}
													helperText={touched.itemQuantity && errors.itemQuantity}
													size={"small"}
													InputProps={{
														readOnly: true,
													}}
													style={{
														backgroundColor: "#c4c4c4",
													}}
													inputRef={stockListRef}
													onMouseEnter={() => setOpenStockList(true)}
													onMouseLeave={() => setOpenStockList(false)}
												/>
												{type === 'update' && stockList.length > 0 && (
													<Popper
														open={openStockList}
														anchorEl={stockListRef.current}
														placement='bottom-start'
														transition
														disablePortal
														onMouseEnter={() => setOpenStockList(true)}
														onMouseLeave={() => setOpenStockList(false)}
														style={{ zIndex: 2000 }}
													>
														{({ TransitionProps }) => (
															<Fade {...TransitionProps}>
																<Paper style={{
																	padding: '10px',
																	backgroundColor: '#fff',
																	border: '1px solid #dee2e6',
																	borderRadius: '4px',
																	boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
																}}>
																	<Typography className={classes.typography}>
																		{stockList && stockList.length > 0 && (
																			<div style={{ marginBottom: "15px" }}>
																				<div style={{fontSize: '14px', marginBottom: '8px'}}><strong>Stock</strong></div>
																				{stockList.map((stock, index) => (
																					<div key={index} style={{ marginBottom: 8, fontSize: '12px' }}>
																						<span>{stock.calculate_quantity}</span> –{" "}
																						<span>{stock.warehouse_name}</span> –{" "}
																						<span>{stock.location_name}</span>
																					</div>
																				))}
																			</div>
																		)}
																		{acceptAndPackedOrder && acceptAndPackedOrder.length > 0 && (
																			<div style={{ marginBottom: "15px" }}>
																				<div style={{fontSize: '14px', marginBottom: '8px'}}><strong>Reserved in customer order</strong></div>
																				{acceptAndPackedOrder.map((order, index) => (
																					<div key={index} style={{ marginBottom: 8, fontSize: '12px' }}>
																						order id : <span>{order.order_id}</span> - quantity : <span>{order.quantity}</span>
																					</div>
																				))}
																			</div>
																		)}
																		{availableForSale !== undefined && availableForSale !== null && (
																			<div>
																				<span style={{ fontWeight: 800, fontSize: '12px' }}>Available for sales: {availableForSale}</span>
																			</div>
																		)}
																	</Typography>
																</Paper>
															</Fade>
														)}
													</Popper>
												)}
											</Grid> */}
											{/* dietary preferences */}
											<Grid item xs={12}>
												<Typography>Dietary preferences</Typography>
												<SelectAndSearchComponent
													options={dietaryPreferences}
													value={values.itemDietary}
													onChange={(e) => {
														setFieldValue("itemDietary", e);
													}}
													placeholder="Select Dietary preferences"
													getOptionValue={(opt) => opt.name}
													getOptionLabel={(diet) => (
														<div style={{display: "flex", alignItems: "center", gap: "5px"}}>
															<img src={diet?.image_url} style={{width: "20px", objectFit: "contain"}}
															     alt=""/> {diet.name}
														</div>
													)}
													isMulti={true}
												/>
											</Grid>
											{/* product tags*/}
											{/*<Grid item xs={12}>*/}
											{/*	<Typography>Product tags</Typography>*/}
											{/*	<SelectAndSearchComponent*/}
											{/*		options={props.category}*/}
											{/*		value={values.itemCat}*/}
											{/*		onChange={(e) => {*/}
											{/*			setFieldValue("itemCat", e);*/}
											{/*			// also fetch subcategory map if action available*/}
											{/*			props.getSubCategoryMap && props.getSubCategoryMap({category_id: e?.list_categories?.[0]?.id});*/}
											{/*			setFieldValue("itemSubCat", null);*/}
											{/*		}}*/}
											{/*		placeholder="Select Product tags"*/}
											{/*		getOptionValue={(opt) => opt.list_categories?.[0]?.name}*/}
											{/*		getOptionLabel={(cat) => (*/}
											{/*			<div style={{display: "flex", alignItems: "center", gap: "5px"}}>*/}
											{/*				<img src={cat?.list_categories?.[0]?.image} style={{width: "20px", objectFit: "contain"}}*/}
											{/*				     alt=""/> {cat.list_categories?.[0]?.name}*/}
											{/*			</div>*/}
											{/*		)}*/}
											{/*		isMulti={true}*/}
											{/*	/>*/}
											{/*</Grid>*/}
										</Grid>
									</Grid>
									<Grid item xs={12} md={3}>
										<Grid>
											{/*images cards section*/}
											<Grid item xs={12}>
												<Box className={classes.rightCard + " mt-4"}>
													{/* Image Preview */}
													<Box display="flex" justifyContent="center" mb={2}>
														<ImagesCarousel 
															sliders={values.slider || initialValues.slider} 
															image={values.image || initialValues.image}
															onRemove={(slider) => handleRemoveSliders(slider, values, setFieldValue)}
														/>
													</Box>
													{/* Single Image Upload */}
													<Grid container spacing={1} xs={12} alignItems="center">
														{/* Single Image Upload */}
														<Grid item xs={12} md={6}>
															<input
																accept="image/*"
																id="upload-image"
																type="file"
																style={{display: "none"}}
																onChange={(e) => {
																	uploadProductImage(e, setFieldValue);
																	// Don't reset the input value to allow re-uploads of same file
																}}
															/>
															<label htmlFor="upload-image" className={"w-100"}>
																<Button
																	variant="contained"
																	color={values.image ? "secondary" : "primary"}
																	component="span"
																	size={"small"}
																	fullWidth
																	disabled={imageUploading}
																>
																	{imageUploading ? "Uploading..." : (values.image ? "✓ Image Uploaded" : "Upload main image")}
																</Button>
															</label>
														</Grid>

														{/* Multiple Slider Upload */}
														<Grid item xs={12} md={6}>
															<input
																accept="image/*"
																id="upload-sliders"
																type="file"
																multiple
																style={{display: "none"}}
																onChange={(e) => uploadProductSliders(e, setFieldValue, values)}
															/>
															<label htmlFor="upload-sliders" className={"w-100"}>
																<Button
																	variant="contained"
																	color="primary"
																	component="span"
																	size={"small"}
																	fullWidth
																>
																	{(values?.slider?.length || 0) > 0
																		? `${values?.slider?.length} file(s) selected`
																		: "Upload extra images"}
																</Button>
															</label>
														</Grid>
													</Grid>
													{/* Hidden fields to track image and slider in Formik state */}
													<input type="hidden" name="image" value={values.image || ''} />
													<input type="hidden" name="slider" value={JSON.stringify(values.slider || [])} />
												</Box>
											</Grid>
											{/* pricing section */}
											<Grid item xs={12}>
												<Box className={classes.rightCard + " mt-4"}>
													<Typography>PRICING</Typography>
													<Grid container xs={12} alignItems="center" className={"mt-2 ml-1"}>
														{/* Sales Price ($) */}
														<Grid item xs={9}>
															<Box mb={1} display="flex" alignItems="center" gap={1}>
																<Typography>Sales Price($)</Typography>
																{type === 'update' && itemId && (
																	<div
																		ref={(ref) => setPriceHistoryRef('main-price', ref)}
																		onMouseEnter={() => handlePriceHistoryEnter('main-price', itemId)}
																		onMouseLeave={() => handlePriceHistoryLeave('main-price')}
																		style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
																	>
																		<QueryBuilder fontSize="small" style={{ color: '#1976d2' }} />
																	</div>
																)}
															</Box>
															<TextField
																fullWidth
																type="number"
																name="itemPrice"
																label="Price (Selling price - USD)"
																variant="outlined"
																className={classes.field}
																value={values.itemPrice}
																onChange={handleChange}
																error={touched.itemPrice && Boolean(errors.itemPrice)}
																helperText={touched.itemPrice && errors.itemPrice}
																size={"small"}
															/>

															{/* Price History Popper for Sales Price */}
															{type === 'update' && itemId && (
																<Popper
																	open={openPriceHistory['main-price'] || false}
																	anchorEl={priceHistoryRefs.current['main-price']}
																	placement='bottom-start'
																	transition
																	disablePortal
																	modifiers={{
																		flip: {
																			enabled: false,
																		},
																		preventOverflow: {
																			enabled: true,
																			boundariesElement: 'viewport',
																		},
																	}}
																	onMouseEnter={() => handlePriceHistoryEnter('main-price', itemId)}
																	onMouseLeave={() => handlePriceHistoryLeave('main-price')}
																	style={{ zIndex: 2000, width: 400 }}
																>
																	{({ TransitionProps }) => (
																		<Fade {...TransitionProps}>
																			<Paper style={{
																				width: '100%',
																				padding: '10px',
																				backgroundColor: '#fff',
																				border: '1px solid #dee2e6',
																				borderRadius: '4px',
																				boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
																			}}>
																				<Typography className={classes.typography}>
																					<div style={{fontSize: '14px'}}><strong>Sales Price History</strong></div>
																					{loadingPriceHistory['main-price'] ? (
																						<div style={{ textAlign: 'center', padding: '20px' }}>
																							<CircularProgress size={20} />
																						</div>
																					) : priceHistoryData['main-price'] && priceHistoryData['main-price'].length > 0 ? (
																						<Table striped bordered hover size="sm" style={{ marginTop: '10px', marginBottom: '0', fontSize: '10px' }}>
																							<thead>
																								<tr style={{ backgroundColor: '#f8f9fa' }}>
																									<th className="text-center" style={{ border: '1px solid #dee2e6', padding: '6px', fontSize: '10px' }}>Date From</th>
																									<th className="text-center" style={{ border: '1px solid #dee2e6', padding: '6px', fontSize: '10px' }}>Date To</th>
																									<th className="text-center" style={{ border: '1px solid #dee2e6', padding: '6px', fontSize: '10px' }}>Price</th>
																								</tr>
																							</thead>
																							<tbody>
																								{priceHistoryData['main-price'].map((history, index) => (
																									<tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa' }}>
																										<td className="text-center" style={{ border: '1px solid #dee2e6', padding: '6px', fontSize: '10px' }}>{history.date_from}</td>
																										<td className="text-center" style={{ border: '1px solid #dee2e6', padding: '6px', fontSize: '10px' }}>{history.date_to || 'Active'}</td>
																										<td className="text-center" style={{ border: '1px solid #dee2e6', padding: '6px', fontSize: '10px' }}>${history.price}</td>
																									</tr>
																								))}
																							</tbody>
																						</Table>
																					) : (
																						<div style={{ textAlign: 'center', padding: '20px' }}>
																							<small style={{fontSize: '10px'}}>No price history found</small>
																						</div>
																					)}
																				</Typography>
																			</Paper>
																		</Fade>
																	)}
																</Popper>
															)}
														</Grid>
														{/* COST price ($)
														<Grid item xs={9}>
															<Box mb={1}>
																<Typography>Cost Price($)</Typography>
															</Box>
															<TextField
																fullWidth
																type="number"
																name="itemMrp"
																label="COST ($)"
																variant="outlined"
																className={classes.field}
																value={values.itemMrp}
																onChange={handleChange}
																error={touched.itemMrp && Boolean(errors.itemMrp)}
																helperText={touched.itemMrp && errors.itemMrp}
																size={"small"}
															/>
														</Grid> */}
														</Grid>

														{/* VAT Rate */}
														<Grid item xs={9}>
															<Box mb={1}>
																<Typography>VAT Rate</Typography>
															</Box>
															<TextField
																fullWidth
																select
																name="vatRate"
																label="VAT Rate"
																variant="outlined"
																className={classes.field}
																value={values.vatRate}
																onChange={handleChange}
																size={"small"}
																SelectProps={{
																	native: true,
																}}
															>
																<option value={-1}>VAT Exempt</option>
																<option value={0}>0%</option>
																<option value={10}>10%</option>
															</TextField>
														</Grid>
														{/* Sales Price Section and Promotions */}
														<Grid item xs={12} className={"mt-2"}>
															<Grid container spacing={1} alignItems="center">
																{/* Left: Title + TextField */}
																<Grid item xs={9} container direction="column">
																	<Box mb={1} display="flex" alignItems="center" gap={1}>
																		<Typography
																			variant="subtitle1"
																			sx={{
																				fontWeight: 600,
																				color: 'text.primary',
																				display: 'flex',
																				alignItems: 'center',
																				gap: 1
																			}}
																		>
																			Promotion Price ($)
																		</Typography>
																		{type === 'update' && itemId && (
																			<div
																				ref={(ref) => setPromotionHistoryRef('main-promotion', ref)}
																				onMouseEnter={() => handlePromotionHistoryEnter('main-promotion', itemId)}
																				onMouseLeave={() => handlePromotionHistoryLeave('main-promotion')}
																				style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
																			>
																				<QueryBuilder fontSize="small" style={{ color: '#1976d2' }} />
																			</div>
																		)}
																	</Box>
																	<TextField
																		fullWidth
																		type="number"
																		name="itemPromoPrice"
																		label="Price (Promotion Selling price - USD)"
																		variant="outlined"
																		className={classes.field}
																		value={values.itemPromoPrice}
																		onChange={handleChange}
																		error={touched.itemPromoPrice && Boolean(errors.itemPromoPrice)}
																		helperText={touched.itemPromoPrice && errors.itemPromoPrice}
																		size="small"
																	/>

																	{/* Promotion History Popper */}
																	{type === 'update' && itemId && (
																		<Popper
																			open={openPromotionHistory['main-promotion'] || false}
																			anchorEl={promotionHistoryRefs.current['main-promotion']}
																			placement='bottom-start'
																			transition
																			disablePortal
																			modifiers={{
																				flip: {
																					enabled: false,
																				},
																				preventOverflow: {
																					enabled: true,
																					boundariesElement: 'viewport',
																				},
																			}}
																			onMouseEnter={() => handlePromotionHistoryEnter('main-promotion', itemId)}
																			onMouseLeave={() => handlePromotionHistoryLeave('main-promotion')}
																			style={{ zIndex: 2000, width: 400 }}
																		>
																			{({ TransitionProps }) => (
																				<Fade {...TransitionProps}>
																					<Paper style={{
																						width: '100%',
																						padding: '10px',
																						backgroundColor: '#fff',
																						border: '1px solid #dee2e6',
																						borderRadius: '4px',
																						boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
																					}}>
																						<Typography className={classes.typography}>
																							<div style={{fontSize: '14px'}}><strong>Promotion Price History</strong></div>
																							{loadingPromotionHistory['main-promotion'] ? (
																								<div style={{ textAlign: 'center', padding: '20px' }}>
																									<CircularProgress size={20} />
																								</div>
																							) : promotionHistoryData['main-promotion'] && promotionHistoryData['main-promotion'].length > 0 ? (
																								<Table striped bordered hover size="sm" style={{ marginTop: '10px', marginBottom: '0', fontSize: '10px' }}>
																									<thead>
																										<tr style={{ backgroundColor: '#f8f9fa' }}>
																											<th className="text-center" style={{ border: '1px solid #dee2e6', padding: '6px', fontSize: '10px' }}>Date From</th>
																											<th className="text-center" style={{ border: '1px solid #dee2e6', padding: '6px', fontSize: '10px' }}>Date To</th>
																											<th className="text-center" style={{ border: '1px solid #dee2e6', padding: '6px', fontSize: '10px' }}>Price</th>
																										</tr>
																									</thead>
																									<tbody>
																										{promotionHistoryData['main-promotion'].map((history, index) => (
																											<tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa' }}>
																												<td className="text-center" style={{ border: '1px solid #dee2e6', padding: '6px', fontSize: '10px' }}>{history.date_from}</td>
																												<td className="text-center" style={{ border: '1px solid #dee2e6', padding: '6px', fontSize: '10px' }}>{history.date_to || 'Active'}</td>
																												<td className="text-center" style={{ border: '1px solid #dee2e6', padding: '6px', fontSize: '10px' }}>${history.price}</td>
																											</tr>
																										))}
																									</tbody>
																								</Table>
																							) : (
																								<div style={{ textAlign: 'center', padding: '20px' }}>
																									<small style={{fontSize: '10px'}}>No promotion history found</small>
																								</div>
																							)}
																						</Typography>
																					</Paper>
																				</Fade>
																			)}
																		</Popper>
																	)}
																</Grid>
																{values.itemPromoPrice > 0 && <Grid container spacing={1} className="pb-2">
																	<>
																		<Grid item xs={12}>
																			<Typography variant="caption" color="text.secondary">
																				PROMOTION PERIOD
																			</Typography>
																		</Grid>
																		<Grid container  spacing={1} style={{ width: '100%' }}>
																			<Grid item xs={12} sm={6}>
																				<CustomDatetime
																					label="Start Date & Time"
																					value={values.itemPromoStartDate}
																					onChange={(v) => setFieldValue("itemPromoStartDate", v)}
																				/>
																			</Grid>
																			<Grid item xs={12} sm={6}>
																				<CustomDatetime
																					label="End Date & Time"
																					value={values.itemPromoEndDate}
																					onChange={(v) => setFieldValue("itemPromoEndDate", v)}
																				/>
																			</Grid>
																		</Grid>
																		{/* Duration Display */}
																		{values.itemPromoStartDate && values.itemPromoEndDate && (
																			<Grid item xs={12}>
																				<Typography variant="caption">
																					Promotion
																					duration: {calculateDuration(values.itemPromoStartDate, values.itemPromoEndDate)}
																				</Typography>
																			</Grid>
																		)}
																	</>
																</Grid>}
															</Grid>
														</Grid>

														{/* Delipoints Promotion Section */}
														<Grid item xs={12} className={"mt-2"}>
															<Grid container spacing={1} alignItems="flex-start">
																<Grid item xs={6} container direction="column">
																	<Box mb={1}>
																		<Typography variant="subtitle1" style={{ fontWeight: 600, color: '#555' }}>
																			Delipoints Price ($)
																		</Typography>
																	</Box>
																	<TextField
																		fullWidth
																		type="number"
																		name="dpPrice"
																		label="Delipoints Price (USD)"
																		variant="outlined"
																		className={classes.field}
																		value={values.dpPrice ?? ""}
																		onChange={handleChange}
																		size="small"
																		inputProps={{ min: 0, step: 0.01 }}
																	/>
																</Grid>
																<Grid item xs={6} container direction="column">
																	<Box mb={1}>
																		<Typography variant="subtitle1" style={{ fontWeight: 600, color: '#555' }}>
																			Delipoints Points
																		</Typography>
																	</Box>
																	<TextField
																		fullWidth
																		type="number"
																		name="dpPoints"
																		label="Delipoints Points"
																		variant="outlined"
																		className={classes.field}
																		value={values.dpPoints ?? ""}
																		onChange={handleChange}
																		size="small"
																		inputProps={{ min: 0, step: 1 }}
																	/>
																</Grid>
																{(values.dpPrice > 0 || values.dpPoints > 0) && (
																	<Grid container spacing={1} className="pb-2">
																		<Grid item xs={12}>
																			<Typography variant="caption" color="textSecondary">
																				DELIPOINTS PROMOTION PERIOD
																			</Typography>
																		</Grid>
																		<Grid container spacing={1} style={{ width: '100%' }}>
																			<Grid item xs={12} sm={6}>
																				<CustomDatetime
																					label="Start Date & Time"
																					value={values.dpPromoStartDate}
																					onChange={(v) => setFieldValue("dpPromoStartDate", v)}
																				/>
																			</Grid>
																			<Grid item xs={12} sm={6}>
																				<CustomDatetime
																					label="End Date & Time"
																					value={values.dpPromoEndDate}
																					onChange={(v) => setFieldValue("dpPromoEndDate", v)}
																				/>
																			</Grid>
																		</Grid>
																		{values.dpPromoStartDate && values.dpPromoEndDate && (
																			<Grid item xs={12}>
																				<Typography variant="caption">
																					Delipoints duration: {calculateDuration(values.dpPromoStartDate, values.dpPromoEndDate)}
																				</Typography>
																			</Grid>
																		)}
																	</Grid>
																)}
															</Grid>
														</Grid>

														{/* Promotion Cost Section - commented out as per requirement */}
														{/* <Grid item xs={12} className={"mt-2"}>
															<Grid container spacing={1}>
																<Grid item xs={12} md={9}>
																	<Box mb={1}>
																		<Typography
																			variant="subtitle1"
																			sx={{
																				fontWeight: 600,
																				color: 'text.primary',
																				display: 'flex',
																				alignItems: 'center',
																				gap: 1
																			}}
																		>
																			Promotion Cost ($)
																		</Typography>
																	</Box>
																	<TextField
																		fullWidth
																		type="number"
																		name="itemPromoCost"
																		label="Promotion Cost - USD"
																		variant="outlined"
																		value={values.itemPromoCost}
																		onChange={handleChange}
																		error={touched.itemPromoCost && Boolean(errors.itemPromoCost)}
																		helperText={touched.itemPromoCost && errors.itemPromoCost}
																		size="small"
																	/>
																</Grid>

																{/* Date Range - Only show when cost > 0 */}
														{/*{values.itemPromoCost > 0 && (*/}
														{/*	<>*/}
														{/*		<Grid item xs={12}>*/}
														{/*			<Typography variant="caption" color="text.secondary">*/}
														{/*				PROMOTION PERIOD*/}
														{/*			</Typography>*/}
														{/*		</Grid>*/}

														{/*		<Grid item xs={12} md={6}>*/}
														{/*			<CustomDatetime*/}
														{/*				label="Promotion Start Date & Time"*/}
														{/*				value={values.itemPromoCostStartDate}*/}
														{/*				onChange={(v) => setFieldValue("itemPromoCostStartDate", v)}*/}
														{/*			/>*/}
														{/*		</Grid>*/}
														{/*		<Grid item xs={12} md={6}>*/}
														{/*			<CustomDatetime*/}
														{/*				label="Promotion End Date & Time"*/}
														{/*				value={values.itemPromoCostEndDate}*/}
														{/*				onChange={(v) => setFieldValue("itemPromoCostEndDate", v)}*/}
														{/*			/>*/}
														{/*		</Grid>*/}

														{/*		{values.itemPromoCostStartDate && values.itemPromoCostEndDate && (*/}
														{/*			<Grid item xs={12}>*/}
														{/*				<Typography variant="caption">*/}
														{/*					Promotion*/}
														{/*					duration: {calculateDuration(values.itemPromoCostStartDate, values.itemPromoCostEndDate)}*/}
														{/*				</Typography>*/}
														{/*			</Grid>*/}
														{/*		)}*/}
														{/*	</>*/}
														{/*)}*/}
														{/*</Grid>*/}
														{/*</Grid> */}
													{/*</Grid>*/}
												</Box>
											</Grid>
											{/* items status/event section */}
											<Grid item xs={12}>
												<Box className={classes.rightCard + " mt-4"}>
													<Grid container xs={12} alignItems="center" className={"mt-1 ml-1"}>
														{/* Add to New Arrival */}
														<Grid item xs={12}>
															<FormControlLabel
																control={
																	<Checkbox
																		color={"primary"}
																		checked={values.itemNewArrival}
																		onChange={(e) => setFieldValue("itemNewArrival", e.target.checked)}
																	/>
																}
																label="Add to New Arrival"
															/>
														</Grid>
														{values.itemNewArrival && (
															<Box mt={2} mb={2}>
																<Typography>Till</Typography>
																<CustomDatetime
																	value={values.itemArrivalLimit}
																	onChange={(v) => setFieldValue("itemArrivalLimit", v)}
																/>
															</Box>
														)}
														{/* Priority */}
														<Grid item xs={12}>
															<FormControlLabel
																control={
																	<Checkbox
																		checked={values.itemPriority}
																		color={"primary"}
																		onChange={(e) => setFieldValue("itemPriority", e.target.checked)}
																	/>
																}
																label="Priority (boost in search results)"
															/>
														</Grid>
														{/* Same-day delivery */}
														<Grid item xs={12}>
															<FormControlLabel
																control={
																	<Checkbox
																		checked={values.itemSameDay}
																		color={"primary"}
																		onChange={(e) => setFieldValue("itemSameDay", e.target.checked)}
																	/>
																}
																label="Same day delivery"
															/>
														</Grid>
														{/* Add to Trending */}
														<Grid item xs={12}>
															<FormControlLabel
																control={
																	<Checkbox
																		checked={values.itemTrending}
																		color={"primary"}
																		fullWidth
																		onChange={(e) => setFieldValue("itemTrending", e.target.checked)}
																	/>
																}
																label="Add to Trending"
															/>
														</Grid>
														{/* Disable Pay on Delivery */}
														<Grid item xs={12}>
															<FormControlLabel
																control={
																	<Checkbox
																		checked={values.itemDisablePayOnDelivery}
																		color={"primary"}
																		onChange={(e) => setFieldValue("itemDisablePayOnDelivery", e.target.checked)}
																	/>
																}
																label="Disable Cash on Delivery (COD)"
															/>
														</Grid>
														{/* Available */}
														<Grid item xs={12} className={"mb-2"}>
															<Grid container spacing={1} alignItems="center">
																{/* Button */}
																<Grid item xs={5}>
																	<Button
																		fullWidth
																		onClick={() => setFieldValue("itemAvailable", !values.itemAvailable)}
																		className={`available-button ${
																			values.itemAvailable ? "available" : "not-available"
																		}`}
																	>
																		{values.itemAvailable ? "Enable" : "Disable"}
																	</Button>
																</Grid>

																{/* Quantity display */}
																<Grid item xs={7} style={{display: "flex", flexWrap: "wrap", alignItems: "center"}}>
																	<Typography className="quantity-label">
																		Available quantity:
																	</Typography>
																	<div
																		className="quantity-badge"
																		ref={stockListRef}
																		onMouseEnter={() => setOpenStockList(true)}
																		onMouseLeave={() => setOpenStockList(false)}
																	>
																		{values.itemQuantity || 0}
																	</div>
																	{/* Stock List Popper */}
																	{type == 'update' && stockList.length > 0 && (
																		<Popper
																			open={openStockList}
																			anchorEl={stockListRef.current}
																			placement='bottom-start'
																			transition
																			disablePortal
																			onMouseEnter={() => setOpenStockList(true)}
																			onMouseLeave={() => setOpenStockList(false)}
																			style={{ zIndex: 2000 }}
																		>
																			{({ TransitionProps }) => (
																				<Fade {...TransitionProps}>
																					<Paper style={{
																						padding: '10px',
																						backgroundColor: '#fff',
																						border: '1px solid #dee2e6',
																						borderRadius: '4px',
																						boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
																					}}>
																						<Typography className={classes.typography}>
																							{stockList && stockList.length > 0 && (
																								<div style={{ marginBottom: "15px" }}>
																									<div style={{fontSize: '14px', marginBottom: '8px'}}><strong>Stock</strong></div>
																									{stockList.map((stock, index) => (
																										<div key={index} style={{ marginBottom: 8, fontSize: '12px' }}>
																											<span>{stock.calculate_quantity}</span> –{" "}
																											<span>{stock.warehouse_name}</span> –{" "}
																											<span>{stock.location_name}</span>
																										</div>
																									))}
																								</div>
																							)}
																							{acceptAndPackedOrder && acceptAndPackedOrder.length > 0 && (
																								<div style={{ marginBottom: "15px" }}>
																									<div style={{fontSize: '14px', marginBottom: '8px'}}><strong>Reserved in customer order</strong></div>
																									{acceptAndPackedOrder.map((order, index) => (
																										<div key={index} style={{ marginBottom: 8, fontSize: '12px' }}>
																											order id : <span>{order.order_id}</span> - quantity : <span>{order.quantity}</span>
																										</div>
																									))}
																								</div>
																							)}
																							{availableForSale !== undefined && availableForSale !== null && (
																								<div>
																									<span style={{ fontWeight: 800, fontSize: '12px' }}>Available for sales: {availableForSale}</span>
																								</div>
																							)}
																						</Typography>
																					</Paper>
																				</Fade>
																			)}
																		</Popper>
																	)}
																</Grid>
															</Grid>
														</Grid>
													</Grid>
												</Box>
											</Grid>
										</Grid>
									</Grid>

									{/* Supplier Section with Drag and Drop */}
									<Grid item xs={12}>
										<Typography variant="h6" style={{marginTop: '20px', marginBottom: '10px', fontSize: '20px'}}>
											Supplier Products
										</Typography>
										<SupplierRowScroll>
											<SupplierRowFlex as="div">
												<DragHandleSpacer /> {/* Space for drag handle */}
												<SupplierCol colName="supplier" controlId="formGridSupplierLabel">
													<FormLabel>Supplier</FormLabel>
												</SupplierCol>
												<SupplierCol colName="quantity" controlId="formGridQuantityLabel">
													<FormLabel>Quantity</FormLabel>
												</SupplierCol>
												<SupplierCol colName="unit" controlId="formGridUnitLabel">
													<FormLabel>Unit</FormLabel>
												</SupplierCol>
												<SupplierCol colName="productNumber" controlId="formGridProductNumberLabel">
													<FormLabel>Product Number</FormLabel>
												</SupplierCol>
												<SupplierCol colName="productName" controlId="formGridProductNameLabel">
													<FormLabel>Product Name</FormLabel>
												</SupplierCol>
												<SupplierCol colName="comment" controlId="formGridCommentLabel">
													<FormLabel>Comment</FormLabel>
												</SupplierCol>
												<SupplierCol colName="retailPrice" controlId="formGridRetailPriceLabel">
													<FormLabel>Retail Price</FormLabel>
												</SupplierCol>
												<SupplierCol colName="unitCost" controlId="formGridCostLabel">
													<FormLabel>Unit costs ($)</FormLabel>
												</SupplierCol>
												<SupplierCol colName="promotionPriceDate" controlId="formGridPromotionPriceDateLabel">
													<FormLabel>Promotion Price</FormLabel>
												</SupplierCol>
												{type === 'update' && (
													<>
														<SupplierCol colName="stock" controlId="formGridStockLabel">
															<FormLabel>Stock</FormLabel>
														</SupplierCol>
														<SupplierCol colName="order" controlId="formGridOrderLabel">
															<FormLabel>Position</FormLabel>
														</SupplierCol>
													</>
												)}
												<SupplierCol colName="action" controlId="formGridActionLabel" />
											</SupplierRowFlex>

											<DragDropContext onDragEnd={handleOnDragEnd}>
												<Droppable droppableId="supplier-sections">
													{(provided) => (
														<div {...provided.droppableProps} ref={provided.innerRef}>
															{supplierSections.map((section, index) => (
																<Draggable key={section.element_id} draggableId={section.element_id.toString()} index={index}>
																	{(provided, snapshot) => (
																		<DraggableSupplierRow
																			ref={provided.innerRef}
																			{...provided.draggableProps}
																			isDragging={snapshot.isDragging}
																		>
																			<input type="hidden" value={section.id} />
																			<input type="hidden" name={`order-${section.id}`} value={section.order} />

																			<DragHandle {...provided.dragHandleProps} />

																			<SupplierCol colName="supplier" controlId={`formGridSupplier-${section.element_id}`}>
																				<SelectAndSearchComponent
																					options={props.suppliers}
																					value={section.supplier_set}
																					onChange={e => updateSupplierData(section.element_id, 'supplier', e)}
																					placeholder="Supplier"
																					getOptionValue={(opt) => opt.store_users[0].first_name}
																					className={!!errors[section.element_id]?.supplier ? 'is-invalid' : ''}
																					getOptionLabel={(supplier) => (
																							<div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '14px' }}>
																									{supplier.store_users[0].first_name}
																							</div>
																					)}
																					styles={{
																						control: (base) => ({ ...base, fontSize: '14px', minHeight: '36px', height: '36px' }),
																						menu: (base) => ({ ...base, fontSize: '14px' }),
																						option: (base) => ({ ...base, fontSize: '14px' }),
																						valueContainer: (base) => ({ ...base, padding: '0 6px' }),
																						input: (base) => ({ ...base, margin: '0', padding: '0' })
																					}}
																				/>
																				{errors[section.element_id]?.supplier && (
																					<small style={{color: '#dc3545', fontSize: '10px'}}>{errors[section.element_id]?.supplier}</small>
																				)}
																			</SupplierCol>

																			<SupplierCol colName="quantity" controlId={`formGridQuantity-${section.element_id}`}>
																				<Form.Control
																					type="number"
																					placeholder="00.00"
																					min={0}
																					step={1}
																					required
																					value={section.quantity || ''}
																					isInvalid={!!errors[section.element_id]?.quantity}
																					onChange={(e) => updateSupplierData(section.element_id, 'quantity', e.target.value)}
																					style={{fontSize: '14px'}}
																				/>
																				{errors[section.element_id]?.quantity && (
																					<small style={{color: '#dc3545', fontSize: '10px'}}>{errors[section.element_id]?.quantity}</small>
																				)}
																			</SupplierCol>

																			<SupplierCol colName="unit" controlId={`formGridUnit-${section.element_id}`}>
																				<Form.Control
																					as="select"
																					value={section.unit || ''}
																					required
																					isInvalid={!!errors[section.element_id]?.unit}
																					onChange={e => updateSupplierData(section.element_id, 'unit', e.target.value)}
																					style={{fontSize: '14px'}}
																				>
																					{/* <option>Unit</option> */}
																					<option value={'pcs'}>pcs</option>
																					<option value={'kg'}>kg</option>
																					<option value={'g'}>g</option>
																					<option value={'mg'}>mg</option>
																					<option value={'l'}>l</option>
																					<option value={'ml'}>ml</option>
																					<option value={'cl'}>cl</option>
																				</Form.Control>
																				{errors[section.element_id]?.unit && (
																					<small style={{color: '#dc3545', fontSize: '10px'}}>{errors[section.element_id]?.unit}</small>
																				)}
																			</SupplierCol>

																			<SupplierCol colName="productNumber" controlId={`formGridProductNumber-${section.element_id}`}>
																				<CreatableSelectComponent
																					options={supplierProductNumberMap[section.element_id] || []}
																					value={section.productNumberSet}
																					onChange={(opt) => updateSupplierData(section.element_id, 'productNumber', opt)}
																					placeholder="Number"
																					isDisabled={isDisabled}
																					isLoading={isLoading}
																					isClearable
																					styles={{
																						control: (base) => ({ ...base, fontSize: '14px', minHeight: '36px', height: '36px' }),
																						menu: (base) => ({ ...base, fontSize: '14px' }),
																						option: (base) => ({ ...base, fontSize: '14px' }),
																						valueContainer: (base) => ({ ...base, padding: '0 6px' }),
																						input: (base) => ({ ...base, margin: '0', padding: '0' })
																					}}
																				/>
																				{errors[section.element_id]?.productNumber && (
																					<small style={{color: '#dc3545', fontSize: '10px'}}>{errors[section.element_id]?.productNumber}</small>
																				)}
																			</SupplierCol>

																			<SupplierCol colName="productName" controlId={`formGridProductName-${section.element_id}`}>
																				<CreatableSelectComponent
																					options={supplierProductNameMap[section.element_id] || []}
																					value={section.productNameSet}
																					onChange={(opt) => updateSupplierData(section.element_id, 'productName', opt)}
																					placeholder="Name"
																					isDisabled={isDisabled}
																					isLoading={isLoading}
																					isClearable
																					styles={{
																						control: (base) => ({ ...base, fontSize: '14px', minHeight: '36px', height: '36px' }),
																						menu: (base) => ({ ...base, fontSize: '14px' }),
																						option: (base) => ({ ...base, fontSize: '14px' }),
																						valueContainer: (base) => ({ ...base, padding: '0 6px' }),
																						input: (base) => ({ ...base, margin: '0', padding: '0' })
																					}}
																				/>
																				{errors[section.element_id]?.productName && (
																					<small style={{color: '#dc3545', fontSize: '10px'}}>{errors[section.element_id]?.productName}</small>
																				)}
																			</SupplierCol>

																			<SupplierCol colName="comment" controlId={`formGridComment-${section.element_id}`}>
																				<Form.Control
																					type="text"
																					placeholder="Comment"
																					value={section.comment || ''}
																					onChange={e => updateSupplierData(section.element_id, 'comment', e.target.value)}
																					style={{fontSize: '14px'}}
																				/>
																			</SupplierCol>

																			<SupplierCol colName="retailPrice" controlId={`formGridRetailPrice-${section.element_id}`}>
																				<Form.Control
																					type="number"
																					placeholder="00.00"
																					required
																					value={section.retailPrice || ''}
																					isInvalid={!!errors[section.element_id]?.retailPrice}
																					onChange={e => updateSupplierData(section.element_id, 'retailPrice', e.target.value)}
																					style={{fontSize: '14px'}}
																				/>
																				{errors[section.element_id]?.retailPrice && (
																					<small style={{color: '#dc3545', fontSize: '10px'}}>{errors[section.element_id]?.retailPrice}</small>
																				)}
																			</SupplierCol>

																			<SupplierCol colName="unitCost" controlId={`formGridCost-${section.element_id}`}>
																				<Form.Control
																					type="text"
																					placeholder="00.00"
																					required
																					value={section.unitCost || ''}
																					isInvalid={!!errors[section.element_id]?.unitCost}
																					onChange={e => updateSupplierData(section.element_id, 'unitCost', e.target.value)}
																					style={{fontSize: '14px'}}
																				/>
																				{errors[section.element_id]?.unitCost && (
																					<small style={{color: '#dc3545', fontSize: '10px'}}>{errors[section.element_id]?.unitCost}</small>
																				)}
																			</SupplierCol>

																			<SupplierCol colName="promotionPriceDate" controlId={`formGridPromotionPriceDate-${section.element_id}`}>
																				<Form.Row>
																					<Col
																						xs={3}
																						className="d-flex align-items-center"
																					>
																						<Form.Control
																							type="text"
																							placeholder="00.00"
																							value={section.promotionPrice || ''}
																							onChange={e => updateSupplierData(section.element_id, 'promotionPrice', e.target.value)}
																							isInvalid={!!errors[section.element_id]?.promotionPrice}
																							title={errors[section.element_id]?.promotionPrice || ''}
																							style={{fontSize: '14px'}}
																						/>
																					</Col>
																					<Col
																						xs={7}
																						className="d-flex align-items-center px-1"
																					>
																						<LightPickerInput
																							value={section.promotionPriceDate}
																							onChange={({ value }) => updateSupplierData(section.element_id, 'promotionPriceDate', value)}
																							placeholder="Promotion period"
																							style={{ width: '100%', fontSize: '14px' }}
																						/>
																					</Col>
																					<Col
																						xs={1}
																						className="d-flex align-items-center"
																					>
																						<Clear
																							fontSize="small"
																							style={{ cursor: 'pointer', color: '#dc3545' }}
																							onClick={() => clearPromotionData(section.element_id)}
																							title="Clear promotion data"
																						/>
																					</Col>
																					<Col
																						xs={1}
																						className="d-flex align-items-center"
																					>
																						<div
																							ref={(ref) => setPromotionHistoryRef(section.element_id, ref)}
																							onClick={() => openPromotionHistory[section.element_id] ? handlePromotionHistoryLeave(section.element_id) : handlePromotionHistoryEnter(section.element_id, section.supplierProductID)}
																							role="button" tabIndex={0} aria-label="Supplier promotion history"
																							onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); handlePromotionHistoryEnter(section.element_id, section.supplierProductID); } }}
																							style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
																						>
																							<QueryBuilder fontSize="small" />
																						</div>
																						<Popper
																							open={openPromotionHistory[section.element_id] || false}
																							anchorEl={promotionHistoryRefs.current[section.element_id]}
																							placement='bottom-start'
																							transition
																							disablePortal
																							modifiers={{
																								flip: {
																									enabled: false,
																								},
																								preventOverflow: {
																									enabled: true,
																									boundariesElement: 'viewport',
																								},
																							}}

																							style={{ zIndex: 2000, width: 560, maxWidth: '95vw' }}
																						>
																							{({ TransitionProps }) => (
																								<Fade {...TransitionProps}>
																									<Paper style={{
																										width: '100%',
																										maxHeight: '400px',
																										overflow: 'auto',
																										padding: '10px',
																										backgroundColor: '#fff',
																										border: '1px solid #dee2e6',
																										borderRadius: '4px',
																										boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
																									}}>
																										<Typography className={classes.typography}>
																											<div style={{fontSize: '14px'}}><strong>Promotion History</strong>
																												<button type="button" className="btn btn-sm btn-link float-right" onClick={() => handlePromotionHistoryLeave(section.element_id)}>Close</button></div>
																											{loadingPromotionHistory[section.element_id] ? (
																												<div style={{ textAlign: 'center', padding: '20px' }}>
																													<CircularProgress size={20} />
																												</div>
																											) : promotionHistoryData[section.element_id] && promotionHistoryData[section.element_id].length > 0 ? (
																												<Table striped bordered hover size="sm" style={{ marginTop: '10px', marginBottom: '0', fontSize: '10px' }}>
																													<thead>
																														<tr style={{ backgroundColor: '#f8f9fa' }}>
																															<th className="text-center" style={{ border: '1px solid #dee2e6', padding: '6px', fontSize: '10px' }}>Date From</th>
																															<th className="text-center" style={{ border: '1px solid #dee2e6', padding: '6px', fontSize: '10px' }}>Date To</th>
																															<th className="text-center" style={{ border: '1px solid #dee2e6', padding: '6px', fontSize: '10px' }}>Price</th>
																															<th className="text-center">Action</th>
																														</tr>
																													</thead>
																													<tbody>
																														{promotionHistoryData[section.element_id].map((history, index) => (
																															<tr key={history.id} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa' }}>
																																<td className="text-center" style={{ border: '1px solid #dee2e6', padding: '6px', fontSize: '10px' }}>{formatPromotionDate(history.date_from)}</td>
																																<td className="text-center" style={{ border: '1px solid #dee2e6', padding: '6px', fontSize: '10px' }}>{formatPromotionDate(history.date_to) || 'No end date'}</td>
																																<td className="text-center" style={{ border: '1px solid #dee2e6', padding: '6px', fontSize: '10px' }}>{history.price}</td>
																																<td className="text-center">
																																	<button type="button" className="btn btn-sm btn-outline-danger"
																																		disabled={!!deletingPromotionHistory}
																																		aria-label={`Delete promotion ${history.id}`}
																																		onClick={() => deletePromotionHistory(history, section.supplierProductID)}>
																																		{deletingPromotionHistory === history.id ? 'Deleting…' : 'Delete'}
																																	</button>
																																</td>
																															</tr>
																														))}
																													</tbody>
																												</Table>
																											) : (
																												<div style={{ textAlign: 'center', padding: '20px' }}>
																													<small style={{fontSize: '10px'}}>No promotion history found</small>
																												</div>
																											)}
																										</Typography>
																									</Paper>
																								</Fade>
																							)}

</Popper>
																					</Col>
																				</Form.Row>
																			</SupplierCol>

																			{type === 'update' && (
																				<>
																					<SupplierCol colName="stock" controlId={`formGridStock-${section.element_id}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
																						<span style={{fontSize: '14px'}}>{section.stock ?? 0}</span>
																					</SupplierCol>

																					<SupplierCol colName="order" controlId={`formGridOrder-${section.element_id}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
																						<span style={{fontSize: '14px'}}>{section.order}</span>
																					</SupplierCol>
																				</>
																			)}

																			<SupplierCol colName="action" controlId={`formGridAction-${section.element_id}`}>
																				<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
																					<ToggleOnOffButton
																						checked={section.isEnabled}
																						onToggle={() => toggleSupplierEnabled(section.element_id)}
																					/>
																					<Button
																						style={{ backgroundColor: 'rgb(185, 0, 0)', color: 'white'}}
																						variant="contained"
																						size="small"
																						onClick={() => handleRemoveSupplierSection(section.element_id)}
																					>
																						<DeleteIcon />
																					</Button>
																				</div>
																			</SupplierCol>
																		</DraggableSupplierRow>
																	)}
																</Draggable>
															))}
															{provided.placeholder}
														</div>
													)}
												</Droppable>
											</DragDropContext>
										</SupplierRowScroll>

										<Button
											variant="contained"
											color="primary"
											onClick={handleAddSupplierSection}
											style={{marginBottom: '20px', fontSize: '14px', padding: '6px 14px'}}
										>
											Add Supplier
										</Button>
									</Grid>

									{/* submit button */}
									<Grid item xs={12}>
										{props.loading || isSubmitting ? (
											<CircularProgress/>
										) : (
											<Button type="submit" variant="contained" color="primary">
												{type}
											</Button>
										)}
									</Grid>
								</Grid>
							</TabPanel>
							{/* related products tab*/}
							<TabPanel value={tabValue} index={1}>
								<RelatedProduct props={props} relatedProducts={relatedProducts}/>
							</TabPanel>
						</FormikForm>
					)
				}}
			</Formik>
		</div>
	);
};

//types
TabPanel.propTypes = {
	children: PropTypes.node,
	index: PropTypes.any.isRequired,
	value: PropTypes.any.isRequired,
};

const mapStateToProps = (state) => ({
	products: state.dashboardReducer.products,
	related_products: state.dashboardReducer.related_products,
	rp_success: state.dashboardReducer.rp_success,
	g_loading: state.globalReducer.loading,
	brands: state.dashboardReducer.brands,
	countries: state.dashboardReducer.countries,
	category: state.dashboardReducer.category,
	sub_category: state.dashboardReducer.sub_category,
	suppliers: state.dashboardReducer.suppliers,
	success: state.dashboardReducer.success,
	loading: state.dashboardReducer.loading,
	error_message: state.dashboardReducer.error_message,
});

const mapDispatchToProps = (dispatch) => ({
	addProduct: (data, slider) => addProduct(data, slider, dispatch),
	getProductToRelate: (data) => getProductToRelate(data, dispatch),
	getRelatedProducts: (data, relatedPage, setRelatedPage, handleShowToast) => getRelatedProducts(data, relatedPage, setRelatedPage, handleShowToast, dispatch),
	addRelatedProduct: (data, handleShowToast) => addRelatedProduct(data, handleShowToast, dispatch),
	removeRelatedProduct: (data, handleShowToast) => removeRelatedProduct(data, handleShowToast, dispatch),
	updateProduct: (data, slider, removeSlider) => updateProduct(data, slider, removeSlider, dispatch),
	getBrands: (data) => getBrands(data, dispatch),
	getCountries: (data) => getCountries(data, dispatch),
	getCategory: (data) => getCategory(data, dispatch),
	getSubCategory: (data) => getSubCategory(data, dispatch),
	getSuppliers: (data) => getSuppliers(data, dispatch),
	getSubCategoryMap: (data) => getSubCategoryMap(data, dispatch),
	setError: (data) => setError(data, dispatch),
	setLoading: (data) => setLoading(data, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductManagement);
